import { useState, useEffect, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Link, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { apiTokensApi } from '../../services/integrations';
import { formatDateTime } from '../../i18n/format';
import type { ApiTokenView } from '@traaker/shared';

/**
 * Base URL of the public REST API, derived from the same env-specific
 * `VITE_API_URL` every build already carries (checked in per mode — see
 * .env.development / .env.production) rather than hardcoding the prod host,
 * which would silently be wrong on a dev build. Same reasoning as
 * McpIntegration's `mcpServerUrl()`.
 */
function apiBaseUrl(): string {
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'https://api.traaker.ai/api';
  return `${apiUrl.replace(/\/$/, '')}/public/v1`;
}

/** The OpenAPI document, served unauthenticated by the API itself. */
function specUrl(): string {
  return `${apiBaseUrl()}/openapi.json`;
}

/** The browsable reference. A route in this same app, so it follows the origin
 *  the user is already on — no cross-env link. */
function docsPath(): string {
  return '/api-docs';
}

/** A copy-pasteable first request, with the real key already spliced in — a
 *  customer should never have to hand-edit a template with their own secret. */
function buildCurlExample(token: string): string {
  return `curl -H "Authorization: Bearer ${token}" \\\n  ${apiBaseUrl()}/brands`;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={() => { navigator.clipboard.writeText(value).catch(() => {}); }}
      >
        <ContentCopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

/** One labelled, monospace, copyable URL row. */
function UrlRow({ label, value, href }: { label: string; value: string; href?: string }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 132, flexShrink: 0 }}>
        {label}
      </Typography>
      {href ? (
        <Link
          href={href}
          target="_blank"
          rel="noopener"
          sx={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 13,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {value}
          <OpenInNewIcon sx={{ fontSize: 13, flexShrink: 0 }} />
        </Link>
      ) : (
        <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
      )}
      <CopyButton value={value} label={t('apiTokens.copy')} />
    </Box>
  );
}

/**
 * The API section of the Intégrations tab — the REST API's own surface, separate
 * from the Claude/MCP section above it.
 *
 * Deliberately NOT a clone of McpIntegration: there is no tool catalog (no tools
 * here), no `claude mcp add` command (nothing to install), and no read/write
 * scope selector (every route on /api/public/v1 is a GET, so the control would
 * do nothing). What a REST integrator needs instead is the base URL, where the
 * spec lives, where the human-readable reference lives, and a first request that
 * works — which is what this renders.
 */
export default function ApiIntegration({ entitled, conversationsEnabled }: { entitled: boolean; conversationsEnabled?: boolean }) {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<ApiTokenView[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTokens(await apiTokensApi.list());
    } catch {
      /* no-op */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (entitled) load();
  }, [entitled, load]);

  if (!entitled) {
    return (
      <Box sx={{ maxWidth: 760, mt: 4 }}>
        <Typography variant="h6" fontWeight={700}>
          {t('apiTokens.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('apiTokens.upsell')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760, mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('apiTokens.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('apiTokens.intro')}
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setAddOpen(true)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {t('apiTokens.add')}
        </Button>
      </Box>

      <Box
        sx={{
          mt: 2,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'grey.50',
        }}
      >
        <Stack spacing={1}>
          <UrlRow label={t('apiTokens.servedAt')} value={apiBaseUrl()} />
          <UrlRow label={t('apiTokens.specUrl')} value={specUrl()} href={specUrl()} />
          <UrlRow label={t('apiTokens.docsUrl')} value={docsPath()} href={docsPath()} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('apiTokens.rateLimitNote')}
        </Typography>
      </Box>

      {conversationsEnabled && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3, display: 'block', mb: 1 }}>
            {t('apiTokens.conversations.sectionTitle').toUpperCase()}
          </Typography>
          <Stack spacing={1}>
            <UrlRow label="GET" value={`/brands/:brandId/conversation-personas`} />
            <UrlRow label="GET" value={`/brands/:brandId/conversation-personas/:personaId`} />
            <UrlRow label="GET" value={`/brands/:brandId/conversation-personas/:personaId/rollup`} />
            <UrlRow label="GET" value={`/brands/:brandId/conversation-personas/:personaId/launches/:launchId`} />
          </Stack>
        </Box>
      )}

      {/* Brand Personas (2026-10-11) — separate from the conversation routes above; full CRUD. */}
      {conversationsEnabled && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3, display: 'block', mb: 1 }}>
            {t('apiTokens.personas.sectionTitle').toUpperCase()}
          </Typography>
          <Stack spacing={1}>
            <UrlRow label="GET" value={`/brands/:brandId/brand-personas`} />
            <UrlRow label="GET" value={`/brands/:brandId/brand-personas/:brandPersonaId`} />
            <UrlRow label="POST" value={`/brands/:brandId/brand-personas`} />
            <UrlRow label="PATCH" value={`/brands/:brandId/brand-personas/:brandPersonaId`} />
            <UrlRow label="DELETE" value={`/brands/:brandId/brand-personas/:brandPersonaId`} />
          </Stack>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tokens.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('apiTokens.empty')}
        </Alert>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {tokens.map((tk) => (
            <TokenRow key={tk.id} token={tk} onChange={load} />
          ))}
        </Stack>
      )}

      <AddTokenDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={load} />
    </Box>
  );
}

function TokenRow({ token, onChange }: { token: ApiTokenView; onChange: () => void }) {
  const { t } = useTranslation();
  const remove = async () => {
    await apiTokensApi.revoke(token.id).catch(() => {});
    onChange();
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{token.name}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            <Chip
              size="small"
              label={token.tokenHint}
              variant="outlined"
              sx={{ fontFamily: 'ui-monospace, monospace' }}
            />
            <Chip size="small" label={t('apiTokens.createdAt', { date: formatDateTime(token.createdAt) })} variant="outlined" />
            <Chip
              size="small"
              label={
                token.lastUsedAt
                  ? t('apiTokens.lastUsedAt', { date: formatDateTime(token.lastUsedAt) })
                  : t('apiTokens.neverUsed')
              }
              variant="outlined"
            />
          </Box>
        </Box>
        <IconButton size="small" color="error" onClick={remove} title={t('apiTokens.revoke')}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

function AddTokenDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [createdToken, setCreatedToken] = useState('');

  const reset = () => {
    setName('');
    setErr('');
    setCreatedToken('');
  };
  const close = () => {
    reset();
    onClose();
  };

  const create = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await apiTokensApi.create({ name: name || undefined });
      setCreatedToken(res.token);
      onCreated();
    } catch (e: any) {
      setErr(e.message || t('apiTokens.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('apiTokens.dialog.title')}</DialogTitle>
      <DialogContent>
        {createdToken ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">{t('apiTokens.dialog.tokenWarning')}</Alert>
            <TextField
              label={t('apiTokens.dialog.tokenLabel')}
              value={createdToken}
              fullWidth
              size="small"
              multiline
              maxRows={3}
              InputProps={{ readOnly: true, sx: { fontFamily: 'ui-monospace, monospace', fontSize: 12.5 } }}
            />
            <Button variant="contained" onClick={() => { navigator.clipboard.writeText(createdToken).catch(() => {}); }}>
              {t('apiTokens.dialog.copyToken')}
            </Button>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('apiTokens.dialog.curlHintLabel')}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  borderRadius: 1,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  overflowX: 'auto',
                  fontFamily: 'ui-monospace, monospace',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {buildCurlExample(createdToken)}
              </Box>
              <Button
                size="small"
                sx={{ mt: 0.5 }}
                onClick={() => { navigator.clipboard.writeText(buildCurlExample(createdToken)).catch(() => {}); }}
              >
                {t('apiTokens.dialog.copyCommand')}
              </Button>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {err && <Alert severity="error">{err}</Alert>}
            <TextField
              label={t('apiTokens.dialog.nameLabel')}
              placeholder="Looker Studio"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText={t('apiTokens.dialog.nameHelper')}
            />
            <Alert severity="info">{t('apiTokens.dialog.readOnlyNote')}</Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {createdToken ? (
          <Button onClick={close} variant="contained">
            {t('apiTokens.dialog.done')}
          </Button>
        ) : (
          <>
            <Button onClick={close}>{t('apiTokens.dialog.cancel')}</Button>
            <Button
              variant="contained"
              onClick={create}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('apiTokens.dialog.create')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
